import {
	Button,
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Heading,
	Input,
	InputGroup,
	InputLeftAddon,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	Stack,
	Textarea,
	useToast,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@apollo/client";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { logEvent } from "src/utlis/analytics";
import { Trash2 } from "lucide-react";
import { FaUpload } from "react-icons/fa";
import { format } from "date-fns";
import { PortfolioEmploymentHistoryItem } from "src/types";
import {
	EDIT_EMPLOYMENT_HISTORY_ITEM,
	REMOVE_EMPLOYMENT_HISTORY_ITEM,
} from "src/common/graphql/typed-mutations";
import { employmentEditorSchema } from "../portfolio-input-validation";

interface EmploymentHistoryEditorProps {
	item: PortfolioEmploymentHistoryItem;
	onClose: () => void;
	onSaved?: () => void;
}

// Form schema
const formSchema = employmentEditorSchema
	.extend({
		currentlyWorking: z.boolean().optional(),
		monthlySalary: z.number().optional(),
		proDoesNotWishToDiscloseMonthlySalary: z.boolean().optional(),
		images: z
			.array(
				z.object({
					name: z.string(),
					data: z.string(),
					mimeType: z.string(),
				})
			)
			.optional(),
	})
	.refine(
		(data) => {
			// Ensure we have either a monthly salary or the user doesn't want to disclose
			if (!data.monthlySalary && !data.proDoesNotWishToDiscloseMonthlySalary) {
				return false;
			}

			return true;
		},
		{
			message:
				"Monthly salary is required unless you do not wish to disclose it",
			path: ["monthlySalary"],
		}
	);

export function EmploymentHistoryEditor({
	item,
	onClose,
	onSaved,
}: EmploymentHistoryEditorProps) {
	const toast = useToast();

	const [editEmployment, editEmploymentHandle] = useMutation(
		EDIT_EMPLOYMENT_HISTORY_ITEM,
		{
			onCompleted: () => {
				toast({
					title: "Employment history updated",
					status: "success",
					duration: 3000,
					isClosable: true,
				});
				onSaved?.();
				onClose();
			},
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		}
	);

	const [removeEmployment, removeEmploymentHandle] = useMutation(
		REMOVE_EMPLOYMENT_HISTORY_ITEM,
		{
			onCompleted: () => {
				toast({
					title: "Employment history item removed",
					status: "success",
					duration: 3000,
					isClosable: true,
				});
				onClose();
			},
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		}
	);

	const [existingImagesToKeep, setExistingImagesToKeep] = React.useState<
		string[]
	>([]);
	const [selectedPortfolioImages, setSelectedPortfolioImages] = React.useState<
		string[]
	>([]);

	React.useEffect(() => {
		setExistingImagesToKeep(item.images?.map((img) => img.id) || []);
	}, [item.images]);

	const {
		register,
		watch,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			companyName: item.companyName || "",
			jobTitle: item.jobTitle || "",
			description: item.description || "",
			startDate: item.startedAt
				? format(new Date(item.startedAt), "yyyy-MM-dd")
				: "",
			endDate: item.endedAt ? format(new Date(item.endedAt), "yyyy-MM-dd") : "",
			currentlyWorking: item.endedAt === null,
			monthlySalary: item.monthlySalary || 0,
			proDoesNotWishToDiscloseMonthlySalary:
				item.proDoesNotWishToDiscloseMonthlySalary || false,
		},
	});

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		const files = Array.from(e.target.files);

		files.forEach((file) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (!reader.result) return;

				if (typeof reader.result === "string") {
					const image = reader.result;

					setSelectedPortfolioImages((prev) => [...prev, image]);

					const dataObject = {
						name:
							"job-history-image-" +
							reader.result.split(",")[1].substring(0, 5) +
							`-${Math.random() * 1000}`,
						data: reader.result.split(",")[1],
						mimeType: file.type,
					};

					// Store in form state for submission
					const currentImages = (watch("images") as any[]) || [];
					setValue("images", [...currentImages, dataObject]);
				}
			};

			reader.readAsDataURL(file);
		});
	};

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		const images = data.images || [];
		editEmployment({
			variables: {
				id: item.id,
				companyName: data.companyName,
				jobTitle: data.jobTitle,
				description: data.description,
				startedAt: new Date(data.startDate),
				endedAt: data.currentlyWorking
					? null
					: data.endDate
					? new Date(data.endDate)
					: undefined,
				existingImageIds: existingImagesToKeep,
				images: images.length > 0 ? images : undefined,
				proDoesNotWishToDiscloseMonthlySalary:
					!!data.proDoesNotWishToDiscloseMonthlySalary,
				...(data.monthlySalary && data.monthlySalary > 0
					? { monthlySalary: data.monthlySalary }
					: {}),
			},
		});

		logEvent("pro_portfolio/employment_history_updated");
	};

	const handleDelete = () => {
		removeEmployment({
			variables: {
				id: item.id,
			},
		});

		logEvent("pro_portfolio/employment_history_deleted");
	};

	const currentlyWorking = watch("currentlyWorking");
	const monthlySalary = watch("monthlySalary");
	const proDoesNotWishToDiscloseMonthlySalary = watch(
		"proDoesNotWishToDiscloseMonthlySalary"
	);

	return (
		<div className="py-4 max-h-[80vh] overflow-y-auto">
			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
				<FormControl isInvalid={!!errors.companyName}>
					<FormLabel htmlFor="companyName">Company Name</FormLabel>
					<Input
						id="companyName"
						placeholder="Example: ABC Corporation"
						{...register("companyName")}
						variant="outline"
						rounded={0}
						border={"1px solid #D7D7D7"}
					/>
					<FormErrorMessage>{errors.companyName?.message}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={!!errors.jobTitle}>
					<FormLabel htmlFor="jobTitle">Job Title</FormLabel>
					<Input
						id="jobTitle"
						placeholder="Example: Senior Electrician"
						{...register("jobTitle")}
						variant="outline"
						rounded={0}
						border={"1px solid #D7D7D7"}
					/>
					<FormErrorMessage>{errors.jobTitle?.message}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={!!errors.description}>
					<FormLabel htmlFor="description">Job Description</FormLabel>
					<Textarea
						id="description"
						placeholder="Example: Responsible for handling all electrical installations and maintenance."
						{...register("description")}
						rounded={0}
						border={"1px solid #D7D7D7"}
					/>
					<FormErrorMessage>{errors.description?.message}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={!!errors.startDate}>
					<FormLabel htmlFor="startDate">Start Date</FormLabel>
					<Input
						id="startDate"
						{...register("startDate")}
						type="date"
						variant="outline"
						rounded={0}
						border={"1px solid #D7D7D7"}
					/>
					<FormErrorMessage>{errors.startDate?.message}</FormErrorMessage>
				</FormControl>

				<FormControl className="mt-2">
					<div className="flex items-center gap-2">
						<Checkbox
							id="currentlyWorking"
							colorScheme="brand"
							isChecked={currentlyWorking}
							onChange={(e) => {
								setValue("currentlyWorking", e.target.checked);
							}}
						/>
						<FormLabel
							fontSize={"14px"}
							margin={0}
							fontWeight={"normal"}
							htmlFor="currentlyWorking"
						>
							I currently work here
						</FormLabel>
					</div>
				</FormControl>

				{!currentlyWorking && (
					<FormControl isInvalid={!!errors.endDate}>
						<FormLabel htmlFor="endDate">End Date</FormLabel>
						<Input
							id="endDate"
							{...register("endDate")}
							type="date"
							variant="outline"
							rounded={0}
							border={"1px solid #D7D7D7"}
						/>
						<FormErrorMessage>{errors.endDate?.message}</FormErrorMessage>
					</FormControl>
				)}

				<FormControl isInvalid={!!errors.monthlySalary}>
					<FormLabel htmlFor="monthlySalary">Monthly Salary</FormLabel>
					<div className="flex flex-col gap-1">
						<FormControl
							isInvalid={!!errors.proDoesNotWishToDiscloseMonthlySalary}
						>
							<div className="flex items-center gap-1">
								<Checkbox
									id="proDoesNotWishToDiscloseMonthlySalary"
									colorScheme="brand"
									isChecked={proDoesNotWishToDiscloseMonthlySalary}
									onChange={(e) => {
										setValue(
											"proDoesNotWishToDiscloseMonthlySalary",
											e.target.checked
										);
									}}
									isDisabled={!!(monthlySalary && monthlySalary > 0)}
								/>
								<FormLabel
									fontSize={"14px"}
									color={"gray.500"}
									margin={0}
									fontWeight={"normal"}
									htmlFor="proDoesNotWishToDiscloseMonthlySalary"
								>
									I do not wish to disclose the monthly salary
								</FormLabel>
							</div>
						</FormControl>
						<InputGroup>
							<InputLeftAddon rounded={0} border={"1px solid #D7D7D7"}>
								₦
							</InputLeftAddon>
							<NumberInput
								className="w-full"
								onChange={(_, value) => {
									if (isNaN(value)) {
										setValue("monthlySalary", 0);
										return;
									}
									setValue("monthlySalary", value);
								}}
								isDisabled={!!proDoesNotWishToDiscloseMonthlySalary}
								value={monthlySalary}
								step={10000}
								rounded={0}
								border={"#D7D7D7"}
							>
								<NumberInputField rounded={0} />
								<NumberInputStepper>
									<NumberIncrementStepper />
									<NumberDecrementStepper />
								</NumberInputStepper>
							</NumberInput>
						</InputGroup>
					</div>
					<FormErrorMessage>{errors.monthlySalary?.message}</FormErrorMessage>
				</FormControl>

				<div className="flex flex-col items-start gap-2">
					<div className="flex flex-col gap-1">
						<Heading as="h4" className="!text-xl !font-medium">
							Work Photos
						</Heading>
						<p className="text-sm text-gray-600">
							Photos are optional, but they are what populate your LaborPass
							gallery and help clients see the work you handled in this role.
						</p>
					</div>

					{/* Display existing images */}
					{item.images &&
						item.images.filter((img) => existingImagesToKeep.includes(img.id))
							.length > 0 && (
							<div className="flex flex-wrap gap-4 w-full">
								{item.images
									.filter((img) => existingImagesToKeep.includes(img.id))
									.map((image) => (
										<div key={image.id} className="relative">
											<img
												src={image.thumbnailUrl || undefined}
												alt={image.name}
												className="max-h-[250px] max-w-[300px] rounded border"
											/>
											<Button
												position="absolute"
												top={2}
												right={2}
												size="sm"
												colorScheme="red"
												onClick={() => {
													setExistingImagesToKeep((prev) =>
														prev.filter((id) => id !== image.id)
													);
												}}
											>
												Remove
											</Button>
										</div>
									))}
							</div>
						)}

					{/* Display new images being uploaded */}
					{selectedPortfolioImages.length > 0 && (
						<div className="flex flex-wrap gap-4 w-full">
							{selectedPortfolioImages.map((image, index) => (
								<div key={index} className="relative">
									<img
										src={image}
										alt={`Upload Preview ${index + 1}`}
										className="max-h-[250px] max-w-[300px] rounded border"
									/>
									<Button
										position="absolute"
										top={2}
										right={2}
										size="sm"
										colorScheme="red"
										onClick={() => {
											setSelectedPortfolioImages((prev) =>
												prev.filter((_, i) => i !== index)
											);
											// Also remove from form state
											const currentImages = (watch("images") as any[]) || [];
											setValue(
												"images",
												currentImages.filter((_, i) => i !== index)
											);
										}}
									>
										Remove
									</Button>
								</div>
							))}
						</div>
					)}

					<div className="relative flex gap-4">
						<input
							type="file"
							accept="image/*"
							onChange={handleFileChange}
							id="upload-btn"
							hidden
							multiple
						/>
						<Button
							onClick={() => {
								document.getElementById("upload-btn")?.click();
							}}
							leftIcon={<FaUpload />}
							colorScheme="prussianBlue"
							variant="outline"
						>
							{selectedPortfolioImages.length
								? "Add More Photos"
								: "Choose Photos"}
						</Button>
					</div>
				</div>

				<Stack direction="row" spacing={4} justify="space-between" pt={4}>
					<Button
						colorScheme="red"
						variant="outline"
						leftIcon={<Trash2 className="w-5 h-5" />}
						isLoading={removeEmploymentHandle.loading}
						onClick={handleDelete}
					>
						Delete
					</Button>
					<div>
						<Button variant="ghost" mr={3} onClick={onClose}>
							Close
						</Button>
						<Button
							colorScheme="brand"
							isLoading={editEmploymentHandle.loading}
							type="submit"
						>
							Save
						</Button>
					</div>
				</Stack>
			</form>
		</div>
	);
}
